import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import YearPicker from '../../components/YearPicker';
import { listAnnualTargets, bulkSaveAnnualTargets } from '../../services/annualTargetService';
import { listAssignedCourseCodes } from '../../services/courseCodeCategoryService';

// ─── Year range ───────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toDisplayValue(raw) {
  if (raw === null || raw === undefined || raw === '') return '';
  const n = parseFloat(raw);
  if (isNaN(n)) return '';
  // Format with commas, strip trailing .00
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── Styled components ────────────────────────────────────────────────────────

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 88px;
`;

// Year picker row
const YearPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const YearPickerLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const YearPickerWrap = styled.div`
  width: 180px;
`;

// Stats strip
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

const StatCard = styled.div`
  background: ${(p) => p.$bg || '#ffffff'};
  border: 1px solid ${(p) => p.$border || '#e2e8f0'};
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
`;

const StatValue = styled.span`
  font-size: 26px;
  font-weight: 800;
  color: ${(p) => p.$color || '#0f172a'};
  line-height: 1.2;
`;

// Main table card
const TableCard = styled.section`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
`;

const DirtyBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 700;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  position: relative;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 420px;
`;

const Th = styled.th`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
  white-space: nowrap;
  ${(p) => p.$sticky && `
    position: sticky;
    left: ${p.$stickyLeft !== undefined ? p.$stickyLeft : 0}px;
    z-index: 3;
    box-shadow: inset -2px 0 0 #e2e8f0;
  `}
`;

const Td = styled.td`
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #0f172a;
  vertical-align: middle;
  background: #ffffff;
  ${(p) => p.$sticky && `
    position: sticky;
    left: ${p.$stickyLeft !== undefined ? p.$stickyLeft : 0}px;
    z-index: 1;
    box-shadow: inset -2px 0 0 #e2e8f0;
  `}
`;

const SearchInput = styled.input`
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 14px 8px 36px;
  font-size: 14px;
  color: #0f172a;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7' stroke='%2394a3b8' stroke-width='2'/%3E%3Cpath stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' d='M16.5 16.5l4 4'/%3E%3C/svg%3E") no-repeat 11px center;
  min-width: 220px;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #10b981;
    background-color: #f0fdf4;
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
  }
  &::placeholder {
    color: #94a3b8;
  }
`;

const Tr = styled.tr`
  transition: background 0.12s;
  &:hover td {
    background: #f0fdf4 !important;
  }
  &:last-child td {
    border-bottom: none;
  }
`;

// Editable cell input
const CellInput = styled.input`
  border: 2px solid ${(p) => (p.$dirty ? '#f59e0b' : '#e2e8f0')};
  background: ${(p) => (p.$dirty ? '#fffbeb' : '#f8fafc')};
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 15px;
  font-weight: ${(p) => (p.$dirty ? '700' : '500')};
  color: #0f172a;
  text-align: right;
  width: 200px;
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #10b981;
    background: #f0fdf4;
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
  }
  &::placeholder {
    color: #cbd5e1;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 56px 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const ErrorBanner = styled.div`
  border: 1px solid #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 14px;
  margin-bottom: 16px;
`;

// Sticky save bar
const SaveBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  background: #0f172a;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 -4px 24px rgba(15, 23, 42, 0.25);
  transform: translateY(${(p) => (p.$visible ? '0' : '100%')});
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SaveBarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SaveBarTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
`;

const SaveBarSub = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const SaveBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DiscardBtn = styled.button`
  border: 1px solid #334155;
  background: transparent;
  color: #94a3b8;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: #e2e8f0;
    border-color: #64748b;
  }
`;

const SaveBtn = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.45);
  transition: transform 0.15s, box-shadow 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(16, 185, 129, 0.55);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const DownloadBtn = styled.button`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.35);
  transition: transform 0.15s, box-shadow 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(2, 132, 199, 0.45);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

// Namespaced keys — one per editable column
function rKey(id) { return `${id}_r`; }  // annual_target_regs
function dKey(id) { return `${id}_d`; }  // due_recovery
function nKey(id) { return `${id}_n`; }  // new_income

function rowToVals(r) {
  return {
    [rKey(r.categoryId)]: toDisplayValue(r.annualTargetRegs),
    [dKey(r.categoryId)]: toDisplayValue(r.dueRecovery),
    [nKey(r.categoryId)]: toDisplayValue(r.newIncome),
  };
}

export default function AnnualTarget() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // ── fetch ──────────────────────────────────────────────────────────────────
  async function fetchData(yr) {
    setLoading(true);
    setError(null);
    try {
      const data = await listAnnualTargets({ year: yr });
      const items = data.data || [];
      setRows(items);
      const vals = {};
      items.forEach((r) => Object.assign(vals, rowToVals(r)));
      setValues(vals);
      setOriginal({ ...vals });
    } catch {
      setError('Failed to load annual targets. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // ── dirty tracking ─────────────────────────────────────────────────────────
  const dirtyKeys = Object.keys(values).filter(
    (k) => values[k] !== (original[k] ?? '')
  );
  const dirtyRowIds = [...new Set(dirtyKeys.map((k) => k.replace(/_[a-z]+$/, '')))];
  const isDirty = dirtyRowIds.length > 0;

  // ── cell change ────────────────────────────────────────────────────────────
  function handleChange(key, val) {
    // Trim whitespace/tabs (common when pasting from spreadsheets)
    const trimmed = val.replace(/\s/g, '');
    // Allow digits, commas (thousands separator), and a single decimal point
    if (trimmed !== '' && !/^[\d,]*\.?\d*$/.test(trimmed)) return;
    setValues((prev) => ({ ...prev, [key]: trimmed }));
  }

  function parseVal(v) {
    return parseFloat(String(v).replace(/,/g, '')) || 0;
  }

  function formatVal(v) {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (!n && n !== 0) return '';
    return n % 1 === 0
      ? n.toLocaleString('en-US')
      : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function handleBlur(key) {
    setValues((prev) => ({ ...prev, [key]: formatVal(prev[key]) }));
  }

  // ── discard ────────────────────────────────────────────────────────────────
  function handleDiscard() {
    setValues({ ...original });
  }

  // ── save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    const targets = rows
      .filter((r) => dirtyRowIds.includes(String(r.categoryId)))
      .map((r) => {
        const due = parseVal(values[dKey(r.categoryId)]);
        const inc = parseVal(values[nKey(r.categoryId)]);
        return {
          categoryId:       r.categoryId,
          annualTarget:     due + inc,
          annualTargetRegs: parseVal(values[rKey(r.categoryId)]),
          dueRecovery:      due,
          newIncome:        inc,
        };
      });
    if (!targets.length) return;

    setSaving(true);
    setError(null);
    try {
      const data = await bulkSaveAnnualTargets({ year, targets });
      const items = data.data || [];
      setRows(items);
      const vals = {};
      items.forEach((r) => Object.assign(vals, rowToVals(r)));
      setValues(vals);
      setOriginal({ ...vals });
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: `Annual targets for ${year} have been updated successfully.`,
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save. Please try again.';
      setError(msg);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: msg,
        confirmButtonColor: '#10b981',
      });
    } finally {
      setSaving(false);
    }
  }

  // ── excel download ─────────────────────────────────────────────────────────
  async function handleDownloadExcel() {
    if (!rows.length) return;
    setDownloading(true);
    try {
      // Fetch course codes for all categories in parallel
      const categoryCodeMap = {};
      await Promise.all(
        rows.map(async (r) => {
          try {
            const res = await listAssignedCourseCodes(r.categoryId);
            categoryCodeMap[r.categoryId] = res.data || [];
          } catch {
            categoryCodeMap[r.categoryId] = [];
          }
        })
      );

      // ── Sheet 1: Annual Target ───────────────────────────────────────────
      const s1Data = [
        [`Annual Target — ${year}`],
        [],
        ['#', 'Course Category', 'Target Registration', 'Total Due Recovery (LKR)', 'New Income (LKR)', 'Total Income (LKR)'],
        ...rows.map((r, idx) => {
          const reg = parseVal(values[rKey(r.categoryId)]);
          const due = parseVal(values[dKey(r.categoryId)]);
          const inc = parseVal(values[nKey(r.categoryId)]);
          return [idx + 1, r.categoryName, reg, due, inc, due + inc];
        }),
        [],
        [
          '', 'TOTAL',
          rows.reduce((s, r) => s + parseVal(values[rKey(r.categoryId)]), 0),
          rows.reduce((s, r) => s + parseVal(values[dKey(r.categoryId)]), 0),
          rows.reduce((s, r) => s + parseVal(values[nKey(r.categoryId)]), 0),
          rows.reduce((s, r) => s + parseVal(values[dKey(r.categoryId)]) + parseVal(values[nKey(r.categoryId)]), 0),
        ],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(s1Data);
      ws1['!cols'] = [{ wch: 5 }, { wch: 32 }, { wch: 22 }, { wch: 26 }, { wch: 20 }, { wch: 22 }];
      ws1['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

      // ── Sheet 2: Course Categories with Course Codes ─────────────────────
      const s2Data = [
        [`Course Categories & Course Codes — ${year}`],
        [],
        ['Category', 'Short Name', 'Full Name'],
      ];
      rows.forEach((r) => {
        const codes = categoryCodeMap[r.categoryId] || [];
        if (codes.length === 0) {
          s2Data.push([r.categoryName, '—', '—']);
        } else {
          codes.forEach((c, ci) => {
            s2Data.push([ci === 0 ? r.categoryName : '', c.shortName || '', c.fullName || '']);
          });
        }
        s2Data.push([]); // blank separator between categories
      });
      const ws2 = XLSX.utils.aoa_to_sheet(s2Data);
      ws2['!cols'] = [{ wch: 32 }, { wch: 20 }, { wch: 42 }];
      ws2['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

      // ── Sheet 3: Coordinator Template (Apr year → Mar year+1) ────────────
      const MONTHS = ['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'];
      const nextYear = year + 1;
      const s3Data = [
        [`Coordinator Monthly Target Template — April ${year} to March ${nextYear}`],
        ['Fill in Expected Registrations and Expected Income for each month.'],
        [],
        ['Month', 'Expected Registrations', 'Expected Income (LKR)'],
        ...MONTHS.map((m, idx) => {
          const yr = idx < 9 ? year : nextYear;
          return [`${m} ${yr}`, '', ''];
        }),
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(s3Data);
      ws3['!cols'] = [{ wch: 22 }, { wch: 26 }, { wch: 24 }];
      ws3['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
      ];

      // ── Build & download workbook ────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, `Annual Target ${year}`);
      XLSX.utils.book_append_sheet(wb, ws2, 'Course Categories');
      XLSX.utils.book_append_sheet(wb, ws3, 'Coordinator Template');
      XLSX.writeFile(wb, `Annual_Target_${year}.xlsx`);
    } catch (err) {
      console.error('[AnnualTarget] Excel download failed:', err);
      Swal.fire('Download Failed', 'Unable to generate the Excel file. Please try again.', 'error');
    } finally {
      setDownloading(false);
    }
  }

  // ── search filter + sort by total income desc ──────────────────────────────
  const visibleRows = (search.trim()
    ? rows.filter((r) => r.categoryName.toLowerCase().includes(search.trim().toLowerCase()))
    : [...rows]
  ).sort((a, b) => {
    const incA = parseVal(values[nKey(a.categoryId)]);
    const incB = parseVal(values[nKey(b.categoryId)]);
    return incB - incA;
  });

  // ── derived stats ──────────────────────────────────────────────────────────
  const totalAnnualTargetRegs = rows.reduce((sum, r) => {
    return sum + parseVal(values[rKey(r.categoryId)]);
  }, 0);

  const totalDueRecovery = rows.reduce((sum, r) => {
    return sum + parseVal(values[dKey(r.categoryId)]);
  }, 0);

  const totalNewIncome = rows.reduce((sum, r) => {
    return sum + parseVal(values[nKey(r.categoryId)]);
  }, 0);

  const totalIncome = totalDueRecovery + totalNewIncome;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <AdminSalesLayout title="Annual Target" currentSection="branchAnnualTarget">
      <PageSection>

        {/* Year selector */}
        <YearPickerRow>
          <YearPickerLabel>Year:</YearPickerLabel>
          <YearPickerWrap>
            <YearPicker
              value={String(year)}
              onChange={(yr) => { if (yr) setYear(Number(yr)); }}
              placeholder="Select year"
            />
          </YearPickerWrap>
        </YearPickerRow>

        {/* Stats */}
        <StatsRow>
          <StatCard>
            <StatLabel>Categories</StatLabel>
            <StatValue>{rows.length}</StatValue>
          </StatCard>
          <StatCard $bg="#f5f3ff" $border="#ddd6fe">
            <StatLabel>Target Registration — {year}</StatLabel>
            <StatValue $color="#6d28d9" style={{ fontSize: '20px' }}>
              {totalAnnualTargetRegs > 0 ? totalAnnualTargetRegs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
            </StatValue>
          </StatCard>
          <StatCard $bg="#fff7ed" $border="#fed7aa">
            <StatLabel>Total Due Recovery — {year}</StatLabel>
            <StatValue $color="#c2410c" style={{ fontSize: '20px' }}>
              {totalDueRecovery > 0 ? totalDueRecovery.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
            </StatValue>
          </StatCard>
          <StatCard $bg="#f0fdf4" $border="#bbf7d0">
            <StatLabel>New Income — {year}</StatLabel>
            <StatValue $color="#15803d" style={{ fontSize: '20px' }}>
              {totalNewIncome > 0 ? totalNewIncome.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
            </StatValue>
          </StatCard>
          <StatCard $bg="#eff6ff" $border="#bfdbfe">
            <StatLabel>Total Income — {year}</StatLabel>
            <StatValue $color="#1d4ed8" style={{ fontSize: '20px' }}>
              {totalIncome > 0 ? totalIncome.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
            </StatValue>
          </StatCard>
          {isDirty && (
            <StatCard $bg="#fffbeb" $border="#fcd34d">
              <StatLabel>Unsaved Changes</StatLabel>
              <StatValue $color="#b45309">{dirtyRowIds.length} row{dirtyRowIds.length !== 1 ? 's' : ''}</StatValue>
            </StatCard>
          )}
        </StatsRow>

        {/* Table */}
        <TableCard>
          <CardHeader>
            <div>
              <CardTitle>Annual Target — {year}</CardTitle>
              <CardSubtitle>
                Click any cell to edit. Changes are highlighted until saved.
              </CardSubtitle>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <SearchInput
                type="text"
                placeholder="Search category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <DownloadBtn
                type="button"
                onClick={handleDownloadExcel}
                disabled={downloading || loading || rows.length === 0}
                title="Download Excel report"
              >
                {downloading ? (
                  '⏳ Preparing…'
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download Excel
                  </>
                )}
              </DownloadBtn>
              {isDirty && (
                <DirtyBadge>
                  {dirtyRowIds.length} unsaved row{dirtyRowIds.length !== 1 ? 's' : ''}
                </DirtyBadge>
              )}
            </div>
          </CardHeader>

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th $sticky $stickyLeft={0} style={{ width: '48px' }}>#</Th>
                  <Th $sticky $stickyLeft={48}>Course Category</Th>
                  <Th $right>Target Registration</Th>
                  <Th $right>Total Due Recovery</Th>
                  <Th $right>New Income</Th>
                  <Th $right>Total Income</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <Td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '48px' }}>
                      Loading…
                    </Td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <Td colSpan={6}>
                      <EmptyState>
                        No active course categories found.
                        <br />
                        Add categories under <strong>Course Category</strong> first.
                      </EmptyState>
                    </Td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <Td colSpan={6}>
                      <EmptyState>
                        No categories match &ldquo;{search}&rdquo;.
                      </EmptyState>
                    </Td>
                  </tr>
                ) : (
                  visibleRows.map((row, idx) => {
                    const rk = rKey(row.categoryId);
                    const dk = dKey(row.categoryId);
                    const nk = nKey(row.categoryId);
                    const rVal = values[rk] ?? '';
                    const dVal = values[dk] ?? '';
                    const nVal = values[nk] ?? '';
                    const rDirty = rVal !== (original[rk] ?? '');
                    const dDirty = dVal !== (original[dk] ?? '');
                    const nDirty = nVal !== (original[nk] ?? '');
                    const totalIncome = parseVal(dVal) + parseVal(nVal);
                    return (
                      <Tr key={row.categoryId}>
                        <Td $sticky $stickyLeft={0} style={{ color: '#94a3b8', width: '48px' }}>{idx + 1}</Td>
                        <Td $sticky $stickyLeft={48} style={{ fontWeight: 600 }}>{row.categoryName}</Td>
                        <Td style={{ textAlign: 'right' }}>
                          <CellInput
                            type="text"
                            inputMode="decimal"
                            $dirty={rDirty}
                            value={rVal}
                            placeholder="0"
                            onChange={(e) => handleChange(rk, e.target.value)}
                            onBlur={() => handleBlur(rk)}
                          />
                        </Td>
                        <Td style={{ textAlign: 'right' }}>
                          <CellInput
                            type="text"
                            inputMode="decimal"
                            $dirty={dDirty}
                            value={dVal}
                            placeholder="0"
                            onChange={(e) => handleChange(dk, e.target.value)}
                            onBlur={() => handleBlur(dk)}
                          />
                        </Td>
                        <Td style={{ textAlign: 'right' }}>
                          <CellInput
                            type="text"
                            inputMode="decimal"
                            $dirty={nDirty}
                            value={nVal}
                            placeholder="0"
                            onChange={(e) => handleChange(nk, e.target.value)}
                            onBlur={() => handleBlur(nk)}
                          />
                        </Td>
                        <Td style={{ textAlign: 'right', fontWeight: 700, color: '#1d4ed8', fontSize: '15px', paddingRight: '28px' }}>
                          {totalIncome > 0 ? totalIncome.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </TableCard>

      </PageSection>

      {/* ── Sticky save bar ───────────────────────────────────────────────── */}
      <SaveBar $visible={isDirty}>
        <SaveBarLeft>
          <SaveBarTitle>
            {dirtyRowIds.length} row{dirtyRowIds.length !== 1 ? 's' : ''} changed for {year}
          </SaveBarTitle>
          <SaveBarSub>Unsaved changes are highlighted in yellow</SaveBarSub>
        </SaveBarLeft>
        <SaveBarActions>
          <DiscardBtn type="button" onClick={handleDiscard} disabled={saving}>
            Discard
          </DiscardBtn>
          <SaveBtn type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '✓ Save Changes'}
          </SaveBtn>
        </SaveBarActions>
      </SaveBar>
    </AdminSalesLayout>
  );
}
